import { useState, useEffect, useRef, useMemo } from "react";
import { adminApi } from "../../repositories/admin";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../ui/Button";
import { Skeleton } from "../ui/skeleton";
import { useCustomPackage } from "../../hooks/useCustomPackage";

interface Organization {
  id: string;
  name: string;
}

interface InviteDspOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function InviteDspOrganizationModal({ isOpen, onClose }: InviteDspOrganizationModalProps) {
  const [loading, setLoading] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState("");
  const [subscriptionEmail, setSubscriptionEmail] = useState("");

  // Custom package hook
  const { packages, loading: packagesLoading, error: packagesError, fetchCustomPackages } = useCustomPackage();
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);
  const [isPackageDropdownOpen, setIsPackageDropdownOpen] = useState(false);
  const hasFetchedPackages = useRef(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await adminApi.getDspOrganizations();
        setOrganizations(response.data.data || []);
      } catch (error) {
        console.error("Error fetching organizations:", error);
        setSubscriptionMessage("❌ Failed to fetch organizations. Please try again.");
      }
    };

    if (isOpen) {
      fetchOrganizations();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !hasFetchedPackages.current) {
      fetchCustomPackages();
      hasFetchedPackages.current = true;
    }
  }, [isOpen, fetchCustomPackages]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsPackageDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const togglePackageSelection = (id: string) => {
    setSelectedPackages((prev) => {
      if (prev.includes(id)) {
        // Remove package if already selected
        setSubscriptionMessage("");
        return prev.filter((packageId) => packageId !== id);
      } else {
        // Check if we already have 1 package selected
        if (prev.length >= 1) {
          setSubscriptionMessage("⚠️ You can only select one package with $0.00 value.");
          return prev;
        }

        setSubscriptionMessage("");
        return [...prev, id];
      }
    });
  };

  const removePackage = (id: string) => {
    setSelectedPackages((prev) => prev.filter((packageId) => packageId !== id));
  };

  const getPackageName = (id: string) => {
    return packages.find((pkg) => pkg.id === id)?.plan_name || id;
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleInviteSubscription = async () => {
    if (!subscriptionEmail) {
      setSubscriptionMessage("⚠️ Please enter a valid email.");
      return;
    }

    if (!isValidEmail(subscriptionEmail)) {
      setSubscriptionMessage("⚠️ Invalid email format. Please enter a valid email.");
      return;
    }

    if (!selectedOrganization) {
      setSubscriptionMessage("⚠️ Please select an organization.");
      return;
    }

    if (selectedPackages.length !== 1) {
      setSubscriptionMessage("⚠️ Please select exactly 1 package with $0.00 value.");
      return;
    }

    try {
      setLoading(true);
      setSubscriptionMessage("");

      const packageIds = selectedPackages
        .map((id) => {
          const pkg = packages.find((p) => p.id === id);
          return pkg?.package_id || id;
        })
        .join(",");

      await adminApi.inviteDspOrganizationUser({
        email: subscriptionEmail,
        organizationId: selectedOrganization,
        packageId: packageIds
      });
      setSubscriptionMessage("✅ Subscription invite sent successfully!");
      setSubscriptionEmail("");
      setSelectedOrganization("");
      setSelectedPackages([]);

      // Close modal after 1 second
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error: any) {
      console.error("Error sending subscription invite:", error);
      setSubscriptionMessage(error?.response?.data?.message || "❌ Failed to send subscription invite. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-full max-w-md p-5 bg-white rounded-lg shadow-xl">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 border border-transparent"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>

        <h2 className="mb-2 text-lg font-semibold text-black">
          Invite User
        </h2>
        <p className="mb-4 text-sm text-neutral-600">
          Select an organization, one package with $0.00 value, and enter an email address to send a subscription invite.
        </p>

        <div className="relative mb-3">
          <input
            type="email"
            className="w-full px-3 py-2 text-sm text-black transition-colors bg-white border rounded-md outline-none border-neutral-300 placeholder-neutral-400 focus:border-primary-500"
            placeholder="Enter user email..."
            value={subscriptionEmail}
            onChange={(e) => setSubscriptionEmail(e.target.value)}
          />
        </div>

        <div className="relative mb-3">
          <select
            className="w-full px-3 py-2 text-sm text-black transition-colors bg-white border rounded-md outline-none border-neutral-300 placeholder-neutral-400 focus:border-primary-500"
            value={selectedOrganization}
            onChange={(e) => setSelectedOrganization(e.target.value)}
          >
            <option value="" disabled>
              Select an organization
            </option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        </div>

        <div className="relative mb-3" ref={dropdownRef}>
          <div
            className="w-full min-h-[40px] px-3 py-2 text-sm transition-colors bg-white border rounded-md cursor-pointer border-neutral-300 focus-within:border-primary-500"
            onClick={() => !packagesLoading && setIsPackageDropdownOpen(!isPackageDropdownOpen)}
          >
            <div className="flex flex-wrap gap-1.5">
              {selectedPackages.length > 0 ? (
                selectedPackages.map((id) => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium border rounded-full text-primary-700 bg-primary-50 border-primary-200"
                  >
                    {getPackageName(id)}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removePackage(id);
                      }}
                      className="transition-colors rounded text-primary-700 hover:text-primary-800 focus:outline-none"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))
              ) : packagesLoading ? (
                // Placeholder in the trigger rather than "Loading packages..." text.
                <Skeleton className="w-32 h-4 my-1" />
              ) : (
                <span className="text-neutral-400 py-0.5">Select packages</span>
              )}
            </div>
          </div>

          {isPackageDropdownOpen && !packagesLoading && (
            <div className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border rounded-md shadow-lg border-neutral-200 max-h-48 scrollbar-thin">
              {packages.filter((pkg) => parseFloat(pkg.amount) === 0).length === 0 ? (
                <div className="px-3 py-2 text-sm text-center text-neutral-500">No packages available</div>
              ) : (
                packages.filter((pkg) => parseFloat(pkg.amount) === 0).map((pkg) => {
                  const isSelected = selectedPackages.includes(pkg.id);

                  // Check if this package should be disabled
                  // Disable all other packages if one is already selected
                  let isDisabled = false;
                  if (!isSelected && selectedPackages.length >= 1) {
                    isDisabled = true;
                  }

                  return (
                    <div
                      key={pkg.id}
                      className={`flex items-center gap-2 px-3 py-2 text-sm ${
                        isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-primary-50"
                      } ${isSelected ? "bg-primary-50" : ""}`}
                      onClick={() => !isDisabled && togglePackageSelection(pkg.id)}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => {}}
                        className="w-4 h-4 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700 disabled:cursor-not-allowed"
                      />

                      <span className="flex-1">{pkg.plan_name} - ${pkg.amount}/{pkg.interval}</span>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {packagesError && (
            <p className="mt-1 text-xs text-danger-600">Failed to load packages</p>
          )}
        </div>

        <Button
          className="w-full mt-4"
          onClick={handleInviteSubscription}
          loading={loading}
        >
          {loading ? "Sending..." : "Send Invite"}
        </Button>

        {subscriptionMessage && (
          <p className="mt-3 text-sm text-center text-neutral-700">{subscriptionMessage}</p>
        )}
      </div>
    </div>
  );
}

